import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import api from '@/services/apiClient';
import PageHeader from '@/components/ui/PageHeader';
import Card from '@/components/ui/Card';

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('pt-BR'));
const money = (n) => (n == null ? '—' : `R$ ${Number(n).toLocaleString('pt-BR')}`);

/** Stat card no estilo do mock: label mono 10px low / valor Oswald 700 28px / delta volt. */
function StatCard({ label, value, delta, tone = 'hi' }) {
  const valueColor = tone === 'frost' ? 'var(--frost)' : 'var(--hi)';
  return (
    <Card padding="sm" style={{ padding: 14 }}>
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 10,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: 'var(--low)',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 700,
          fontSize: 28,
          lineHeight: 1,
          color: valueColor,
          marginTop: 8,
        }}
      >
        {value}
      </div>
      {delta && (
        <div style={{ fontSize: 10.5, color: 'var(--volt)', marginTop: 6, fontWeight: 600 }}>
          {delta}
        </div>
      )}
    </Card>
  );
}

/** Barra de progresso (trilho surf2 / preenchimento por tom). */
function SourceBar({ label, value, pct, tone }) {
  const color = tone === 'frost' ? 'var(--frost)' : tone === 'mid' ? 'var(--mid)' : 'var(--volt)';
  return (
    <div style={{ marginBottom: 14 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 7,
        }}
      >
        <span style={{ fontSize: 13, color: 'var(--hi)', fontWeight: 600 }}>{label}</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--mid)' }}>
          {value} · {pct}%
        </span>
      </div>
      <div style={{ height: 9, borderRadius: 5, background: 'var(--surf2)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 5 }} />
      </div>
    </div>
  );
}

/** Linha da fila & alertas: dot + texto + chevron. */
function QueueRow({ text, tone, highlight }) {
  const color =
    tone === 'frost'
      ? 'var(--frost)'
      : tone === 'danger'
        ? 'var(--danger)'
        : tone === 'mid'
          ? 'var(--mid)'
          : 'var(--volt)';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 11,
        padding: 11,
        borderRadius: 10,
        background: highlight ? 'color-mix(in srgb, var(--volt) 10%, transparent)' : 'transparent',
      }}
    >
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flex: 'none' }} />
      <span style={{ flex: 1, fontSize: 13.5, color: 'var(--hi)' }}>{text}</span>
      <ChevronRight size={16} strokeWidth={2} color="var(--mid)" />
    </div>
  );
}

export default function AdminDashboard() {
  const { t } = useTranslation();
  const [m, setM] = useState(null);

  useEffect(() => {
    api
      .get('/metrics/admin')
      .then((res) => setM(res.data || res))
      .catch(() => setM(null));
  }, []);

  // Mini bar chart de crescimento (6 colunas, última em destaque).
  const growth = (m?.growth || []).slice(-6).map((g) => g.count);
  const maxGrowth = Math.max(...growth, 1);

  // Receita por fonte (saas/ai/commission) com percentual sobre o total.
  const rev = m?.revenueBySource || {};
  const revTotal = (rev.saas || 0) + (rev.ai || 0) + (rev.commission || 0);
  const pctOf = (v) => (revTotal > 0 ? Math.round((Number(v || 0) / revTotal) * 100) : 0);

  const queue = m?.queue || {};

  return (
    <>
      <PageHeader kicker="Movez · Admin" title={t('nav.dashboard')} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 13 }}>
        <StatCard label={t('dashboard.users')} value={fmt(m?.totalUsers)} delta="+9,2%" />
        <StatCard label={t('dashboard.trainers')} value={fmt(m?.totalTrainers)} delta={t('dashboard.deltaTrainers')} />
        <StatCard label={t('finance.mrr')} value={m?.mrr == null ? '—' : `R$${fmt(m.mrr)}`} delta="+12%" />
        <StatCard
          label={t('dashboard.aiSubscriptions')}
          value={fmt(m?.aiSubscriptions)}
          delta="+31%"
          tone="frost"
        />
        <StatCard label={t('dashboard.commissions')} value={money(m?.commissions)} delta="+7%" />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.5fr 1fr',
          gap: 13,
          marginTop: 13,
        }}
      >
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 18 }}>
            <h3
              style={{
                fontFamily: 'var(--font-display)',
                fontWeight: 600,
                fontSize: 16,
                color: 'var(--hi)',
              }}
            >
              {t('dashboard.revenueBySource')}
            </h3>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--mid)' }}>
              MRR · {m?.mrr == null ? '—' : `R$${fmt(m.mrr)}`}
            </span>
          </div>

          <SourceBar
            label={t('dashboard.sourceSaas')}
            value={money(rev.saas)}
            pct={pctOf(rev.saas)}
            tone="volt"
          />
          <SourceBar
            label={t('dashboard.sourceAi')}
            value={money(rev.ai)}
            pct={pctOf(rev.ai)}
            tone="frost"
          />
          <SourceBar
            label={t('dashboard.sourceCommission')}
            value={money(rev.commission)}
            pct={pctOf(rev.commission)}
            tone="mid"
          />

          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 8,
              height: 64,
              marginTop: 18,
              paddingTop: 16,
              borderTop: '1px solid var(--line)',
            }}
          >
            {(growth.length ? growth : [40, 55, 48, 70, 62, 88]).map((v, i, arr) => {
              const h = Math.max(8, Math.round((v / (growth.length ? maxGrowth : 88)) * 48));
              const last = i === arr.length - 1;
              return (
                <div
                  key={i}
                  style={{
                    flex: 1,
                    height: h,
                    borderRadius: 4,
                    background: last ? 'var(--grad)' : 'var(--surf2)',
                  }}
                />
              );
            })}
          </div>
        </Card>

        <Card>
          <h3
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 600,
              fontSize: 16,
              color: 'var(--hi)',
              marginBottom: 14,
            }}
          >
            {t('dashboard.queueAlerts')}
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <QueueRow
              text={t('dashboard.pendingTrainers', { count: queue.pendingTrainers ?? 0 })}
              tone="volt"
              highlight={(queue.pendingTrainers ?? 0) > 0}
            />
            <QueueRow
              text={t('dashboard.pendingPackages', { count: queue.pendingPackages ?? 0 })}
              tone="frost"
            />
          </div>
        </Card>
      </div>
    </>
  );
}
